import React from 'react';
import { useTranslation } from 'react-i18next';
import Chart from 'react-apexcharts';
import { formatCompactNumber, formatAmount, getActiveCurrency } from '../../Shared/Formatters';
import { useThemePalette } from '../../hooks/useThemePalette';

/**
 * Line chart widget — Trading Density, Predictive Growth, generic time series.
 */
export default function LineChartWidget({ data }) {
    const { t } = useTranslation();
    // Premium skins recolour the line live; null in classic → original blue/cyan.
    const palette = useThemePalette();
    const colors = palette ? [palette[0], palette[1]] : ['#3B82F6', '#06B6D4'];

    if (!data?.data) return null;
    const d = data.data;

    // Predictive Growth shape: { ytdActual, projectedTotal, actualMonths: [{year, month, total}], remainingMonths: [{year, month, projected}] }
    if (d.projectedTotal !== undefined && Array.isArray(d.remainingMonths)) {
        const mk = (y, m) => `${y}-${String(m).padStart(2, '0')}`;
        const months = d.remainingMonths;

        // Actual line: prefer the monthly actual series (draws a real line);
        // fall back to a single YTD point for older backends that don't send
        // actualMonths, so nothing breaks if FE ships ahead of BE.
        const actualPts = (Array.isArray(d.actualMonths) && d.actualMonths.length)
            ? d.actualMonths.map(a => ({ x: mk(a.year, a.month), y: a.total }))
            : [{ x: t('YTD'), y: d.ytdActual }];
        const actual = { name: t('Actual'), data: actualPts };

        // Bridge the projected line back to the last actual point so the two
        // lines connect at the handoff instead of floating apart.
        const bridge = actualPts.length ? [actualPts[actualPts.length - 1]] : [];
        const forecast = { name: t('Projected'), data: [...bridge, ...months.map(m => ({
            x: mk(m.year, m.month),
            y: m.projected,
        }))] };
        // Predictive Growth is a sales projection — render as money.
        return renderChart([actual, forecast], colors, true);
    }

    // Generic series: Array of {label, value} or {date, value}. Metric is unknown
    // here (could be footfall / density counts), so leave it unprefixed.
    if (Array.isArray(d)) {
        const series = [{ name: t('Value'), data: d.map(r => ({ x: r.label || r.date || r._id, y: r.value || r.total || r.totalSales || 0 })) }];
        return renderChart(series, colors);
    }

    return <div style={{ padding: 20 }} className="v3-body">{t('Chart data unavailable.')}</div>;
}

function renderChart(series, colors = ['#3B82F6', '#06B6D4'], isMoney = false) {
    const moneyPrefix = (text) => `${getActiveCurrency()} ${text}`;
    const isDark = document.documentElement.getAttribute('data-theme') !== 'light';
    const options = {
        chart: { type: 'line', toolbar: { show: false }, fontFamily: 'Inter, system-ui', background: 'transparent' },
        colors,
        stroke: { width: 2, curve: 'smooth' },
        fill: { type: 'gradient', gradient: { opacityFrom: 0.25, opacityTo: 0.02 } },
        grid: { borderColor: 'var(--v3-chart-grid)', strokeDashArray: 4 },
        xaxis: { labels: { style: { colors: 'var(--v3-text-muted)', fontSize: '10px' } } },
        yaxis: { labels: {
            style: { colors: 'var(--v3-text-muted)', fontSize: '10px', fontFamily: 'JetBrains Mono, monospace' },
            formatter: isMoney ? (val) => moneyPrefix(formatCompactNumber(val)) : formatCompactNumber,
        } },
        tooltip: {
            theme: isDark ? 'dark' : 'light',
            y: { formatter: (val) => (isMoney ? moneyPrefix(formatAmount(val)) : formatAmount(val)) },
        },
        dataLabels: { enabled: false },
        legend: { show: series.length > 1, fontSize: '10px', labels: { colors: 'var(--v3-text-muted)' } },
    };

    return (
        <div style={{ padding: '4px 10px 8px', height: '100%' }}>
            <Chart type="area" options={options} series={series} height="100%" />
        </div>
    );
}
